import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSessionContext } from "@supabase/auth-helpers-react";
import { supabase } from "../supabaseClient";
import { useUser } from "../contexts/UserContext";
import Footer from '../components/Footer'
import { TableSkeleton } from "../components/LoadingSkeleton";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useReactToPrint } from 'react-to-print';
import { handleMobileFriendlyPrint, isMobileDevice } from '../utils/printHelper';
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { CheckCircle2, Calendar } from "lucide-react";

export default function Timesheet() {
  const navigate = useNavigate();
  const { session } = useSessionContext();
  const { userInfo } = useUser();
  const printRef = useRef();

  // STATE
  const [reportData, setReportData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [filterMonth, setFilterMonth] = useState(
    new Date().toISOString().slice(0, 7) // Format: YYYY-MM
  );

  // TOAST
  const [toast, setToast] = useState({ message: "", type: "" });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

  // DAPATKAN NAMA HARI INDONESIA
  const getDayName = (dateStr) => {
    const days = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
    return days[new Date(dateStr).getDay()];
  };

  // HELPER: Get month name in Indonesian
  const getMonthName = (monthStr) => {
    const months = ["Januari", "Februari", "Maret", "April", "Mei", "Juni",
      "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
    const [year, month] = monthStr.split('-');
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  // HELPER: Format date to Indonesian format
  const formatDateID = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // HELPER: Format time from HH:MM:SS to HH.MM
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    return `${parts[0]}.${parts[1]}`;
  };

  // HELPER: Get periode string
  const getPeriode = () => {
    const [year, month] = filterMonth.split('-');
    const daysInMonth = new Date(year, parseInt(month), 0).getDate();
    return `1-${daysInMonth} ${getMonthName(filterMonth)}`;
  };

  // Generate all days of the month
  const generateMonthDays = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
    const days = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${month.padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({
        date: dateStr,
        dayName: getDayName(dateStr),
        dayOfWeek: new Date(dateStr).getDay(), // 0 = Sunday, 6 = Saturday
      });
    }

    return days;
  };

  // LIBUR NASIONAL DAN CUTI BERSAMA INDONESIA 2026
  // Berdasarkan SKB 3 Menteri (Menteri Agama, Menteri Ketenagakerjaan, Menteri PANRB)
  const HOLIDAYS_2026 = {
    // Libur Nasional
    '2026-01-01': 'Tahun Baru 2026 Masehi',
    '2026-01-16': 'Isra Mikraj Nabi Muhammad SAW',
    '2026-02-17': 'Tahun Baru Imlek 2577 Kongzili',
    '2026-03-19': 'Hari Suci Nyepi Tahun Baru Saka 1948',
    '2026-03-21': 'Hari Raya Idul Fitri 1447 Hijriah',
    '2026-03-22': 'Hari Raya Idul Fitri 1447 Hijriah',
    '2026-04-03': 'Wafat Yesus Kristus',
    '2026-04-05': 'Kebangkitan Yesus Kristus (Paskah)',
    '2026-05-01': 'Hari Buruh Internasional',
    '2026-05-14': 'Kenaikan Yesus Kristus',
    '2026-05-27': 'Hari Raya Idul Adha 1447 Hijriah',
    '2026-05-31': 'Hari Raya Waisak 2570 BE',
    '2026-06-01': 'Hari Lahir Pancasila',
    '2026-06-16': 'Tahun Baru Islam 1448 Hijriah',
    '2026-08-17': 'Hari Kemerdekaan Republik Indonesia',
    '2026-08-25': 'Maulid Nabi Muhammad SAW',
    '2026-12-25': 'Hari Raya Natal',
    // Cuti Bersama
    '2026-02-16': 'Cuti Bersama Tahun Baru Imlek',
    '2026-03-18': 'Cuti Bersama Hari Suci Nyepi',
    '2026-03-20': 'Cuti Bersama Hari Raya Idul Fitri',
    '2026-03-23': 'Cuti Bersama Hari Raya Idul Fitri',
    '2026-03-24': 'Cuti Bersama Hari Raya Idul Fitri',
    '2026-05-15': 'Cuti Bersama Kenaikan Yesus Kristus',
    '2026-05-28': 'Cuti Bersama Hari Raya Idul Adha',
    '2026-12-24': 'Cuti Bersama Hari Raya Natal',
  };

  // Helper: Check if date is a holiday
  const isHoliday = (dateStr) => HOLIDAYS_2026[dateStr] || null;

  // FETCH DATA FROM ATTENDANCE AND TIMESHEET
  useEffect(() => {
    const fetchReportData = async () => {
      if (!session?.user) return;

      setLoadingData(true);
      try {
        const [year, month] = filterMonth.split('-');
        const startDate = `${year}-${month}-01`;
        const endDate = `${year}-${month}-${new Date(parseInt(year), parseInt(month), 0).getDate()}`;

        // Fetch attendance data for the month
        const { data: attendanceData, error: attendanceError } = await supabase
          .from("attendance")
          .select("*")
          .eq("user_id", session.user.id)
          .gte("date", startDate)
          .lte("date", endDate)
          .order("date", { ascending: true });

        if (attendanceError) throw attendanceError;

        // Fetch timesheet data for the month
        const { data: timesheetData, error: timesheetError } = await supabase
          .from("timesheet")
          .select("*")
          .eq("user_id", session.user.id)
          .gte("date", startDate)
          .lte("date", endDate)
          .order("date", { ascending: true });

        if (timesheetError) throw timesheetError;

        // Debug: Log fetched data

        // Fetch leave requests for the month
        const { data: leaveData } = await supabase
          .from("leave_requests")
          .select("*")
          .eq("user_id", session.user.id)
          .eq("status", "approved");

        // Generate all days of the month
        const allDays = generateMonthDays(filterMonth);

        // Create a map for quick lookup
        const attendanceMap = new Map();
        (attendanceData || []).forEach(att => {
          attendanceMap.set(att.date, att);
        });

        // Group timesheet entries by date
        const timesheetMap = new Map();
        (timesheetData || []).forEach(ts => {
          if (!timesheetMap.has(ts.date)) {
            timesheetMap.set(ts.date, []);
          }
          timesheetMap.get(ts.date).push(ts);
        });

        // Check if date is within leave period
        const getLeaveInfo = (dateStr) => {
          if (!leaveData) return null;
          return leaveData.find(leave => dateStr >= leave.start_date && dateStr <= leave.end_date);
        };

        // Merge data
        const mergedData = [];
        let rowNo = 1;

        allDays.forEach(day => {
          const attendance = attendanceMap.get(day.date);
          const timesheets = timesheetMap.get(day.date) || [];
          const leaveInfo = getLeaveInfo(day.date);
          const isWeekend = day.dayOfWeek === 0 || day.dayOfWeek === 6;
          const holidayName = isHoliday(day.date); // Check if it's a national holiday

          // Determine status
          let status = 'masuk';
          let statusText = '';

          if (holidayName) {
            status = 'libur';
            statusText = holidayName;
          } else if (leaveInfo) {
            status = leaveInfo.type === 'cuti' ? 'cuti' : 'sakit';
            statusText = leaveInfo.type === 'cuti' ? 'cuti tahunan' : 'izin sakit';
          }

          // If there are timesheet entries for this day
          if (timesheets.length > 0) {
            const currentRowNo = rowNo++; // Same number for all entries in this day
            timesheets.forEach((ts, index) => {
              mergedData.push({
                no: index === 0 ? currentRowNo : '', // Only show number on first row
                day: index === 0 ? day.dayName : '', // Only show day on first row
                date: index === 0 ? day.date : '', // Only show date on first row
                displayDate: day.date, // Keep actual date for formatting
                timeFrom: index === 0 ? formatTime(attendance?.check_in) : '',
                timeTo: index === 0 ? formatTime(attendance?.check_out) : '',
                jiraCode: ts.project_code || '',
                initiative: ts.project_name || '',
                activity: ts.task_description || '',
                status: status,
                statusText: statusText,
                isWeekend: isWeekend,
                isHoliday: !!holidayName,
                isFirstRow: index === 0,
                rowCount: timesheets.length, // How many rows for this day
              });
            });
          } else if (leaveInfo) {
            // Cuti / Sakit
            mergedData.push({
              no: rowNo++,
              day: day.dayName,
              date: day.date,
              displayDate: day.date,
              timeFrom: '',
              timeTo: '',
              jiraCode: '',
              initiative: 'off day',
              activity: statusText,
              status: status,
              statusText: statusText,
              isWeekend: isWeekend,
              isHoliday: !!holidayName,
              isFirstRow: true,
              rowCount: 1,
            });
          } else if (attendance) {
            // Has attendance but no timesheet
            mergedData.push({
              no: rowNo++,
              day: day.dayName,
              date: day.date,
              displayDate: day.date,
              timeFrom: formatTime(attendance.check_in),
              timeTo: formatTime(attendance.check_out),
              jiraCode: '-',
              initiative: '-',
              activity: attendance.check_in_note || '-',
              status: status,
              statusText: '',
              isWeekend: isWeekend,
              isHoliday: !!holidayName,
              isFirstRow: true,
              rowCount: 1,
            });
          } else if (holidayName && new Date(day.date) <= new Date().setHours(23, 59, 59, 999)) {
            // National holiday without data - only include if today or past
            mergedData.push({
              no: rowNo++,
              day: day.dayName,
              date: day.date,
              displayDate: day.date,
              timeFrom: '',
              timeTo: '',
              jiraCode: '',
              initiative: 'Libur',
              activity: holidayName,
              status: 'libur',
              statusText: holidayName,
              isWeekend: isWeekend,
              isHoliday: true,
              isFirstRow: true,
              rowCount: 1,
            });
          }
          // Weekends without data and future dates are skipped — no empty rows
        });

        setReportData(mergedData);
      } catch (error) {
        console.error("Error loading report data:", error);
        showToast(`Error: ${error.message}`, "error");
      } finally {
        setLoadingData(false);
      }
    };

    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user, filterMonth]);

  // GET UPCOMING HOLIDAYS IN SELECTED MONTH
  const getUpcomingHolidays = () => {
    const [, ] = filterMonth.split('-');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcomingHolidays = [];

    Object.entries(HOLIDAYS_2026).forEach(([date, name]) => {
      const holidayDate = new Date(date);
      const holidayMonth = `${holidayDate.getFullYear()}-${String(holidayDate.getMonth() + 1).padStart(2, '0')}`;

      // Only include holidays in selected month that are in the future
      if (holidayMonth === filterMonth && holidayDate > today) {
        upcomingHolidays.push({
          date: date,
          name: name,
          formattedDate: formatDateID(date),
          dayName: getDayName(date)
        });
      }
    });

    // Sort by date
    upcomingHolidays.sort((a, b) => new Date(a.date) - new Date(b.date));

    return upcomingHolidays;
  };

  const upcomingHolidays = getUpcomingHolidays();

  // CALCULATE STATISTICS
  const calculateStats = () => {
    const uniqueDates = new Set();
    let masuk = 0;
    let sakit = 0;
    let cuti = 0;

    reportData.forEach(row => {
      if (!uniqueDates.has(row.date) && row.isFirstRow) {
        uniqueDates.add(row.date);
        if (row.status === 'masuk' && (row.timeFrom || row.jiraCode !== '-')) {
          masuk++;
        } else if (row.status === 'sakit') {
          sakit++;
        } else if (row.status === 'cuti') {
          cuti++;
        }
      }
    });

    return { masuk, sakit, cuti, total: masuk + sakit + cuti };
  };

  const stats = calculateStats();

  // EXPORT CSV - Template ADIDATA BSI (matching PDF format)
  const exportCSV = () => {
    // Header dengan logo placeholder
    const headerInfo = [
      ["", "", "", "", "", "", "", ""],
      ["", "", "Bank Syariah Indonesia", "", "", "", "", ""],
      ["", "", "Technical Project Admin Daily Activity Reports", "", "", "", "", ""],
      [""],
      [`Name`, `${userInfo?.full_name || 'N/A'}`],
      [`Role`, `${userInfo?.position || 'Technical Project Admin'}`],
      [`Periode`, `${getPeriode()}`],
      [`Departement / Group`, `Project Management | IT Digital & Development Group (IDG)`],
      [""],
    ];

    const tableHeaders = ["No", "Day", "Date", "Time From", "Time To", "Kode Project JIRA", "Nama Project Inisiatif", "DAILY ACTIVITY"];

    const tableData = reportData.map((e) => [
      e.no,
      e.day,
      e.date ? formatDateID(e.date) : '',
      e.timeFrom || '',
      e.timeTo || '',
      e.jiraCode || '',
      e.initiative || '',
      e.activity || '',
    ]);

    // Summary section matching PDF
    const summaryData = [
      [""],
      ["STATISTIK", "", "", "", "", "TANDA TANGAN"],
      [""],
      ["JUMLAH HARI MASUK", stats.masuk, "", "", "", "Resource", "Team Leader", "Dept Head"],
      ["SAKIT", stats.sakit],
      ["CUTI", stats.cuti],
      ["", "", "", "", "", "", "", ""],
      ["TOTAL", stats.total, "", "", "", `(${userInfo?.full_name || 'N/A'})`, "(Ressa Fauzi)", "(Muhammad Akbar)"],
    ];

    const allData = [...headerInfo, tableHeaders, ...tableData, ...summaryData];

    const ws = XLSX.utils.aoa_to_sheet(allData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Timesheet");
    XLSX.writeFile(wb, `Timesheet_${userInfo?.full_name}_${filterMonth}.csv`);
    showToast("CSV berhasil diexport! 📄");
  };

  // EXPORT EXCEL - Template ADIDATA BSI with ExcelJS (includes logo and styling)
  const exportExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Timesheet Report');

      // Set column widths to match PDF layout (9 columns: A-I)
      worksheet.columns = [
        { width: 5 },   // A - No
        { width: 10 },  // B - Day
        { width: 18 },  // C - Date
        { width: 10 },  // D - Time From
        { width: 10 },  // E - Time To
        { width: 18 },  // F - Kode Project JIRA
        { width: 25 },  // G - Nama Project Inisiatif
        { width: 50 },  // H - DAILY ACTIVITY
        { width: 15 },  // I - Extra for layout
      ];

      // Add logos (same paths as PDF)
      try {
        // Load ADIDATA logo (LEFT side - column A) - like PDF
        const adidataLogoResponse = await fetch('/assets/logos/adidata.png');
        const adidataLogoBlob = await adidataLogoResponse.blob();
        const adidataLogoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(adidataLogoBlob);
        });

        const adidataLogoId = workbook.addImage({
          base64: adidataLogoBase64,
          extension: 'png',
        });

        worksheet.addImage(adidataLogoId, {
          tl: { col: 0, row: 0 },
          ext: { width: 90, height: 45 }
        });

        // Load BSI logo (RIGHT side - column H/I) - like PDF
        const bsiLogoResponse = await fetch('/assets/logos/logobsi.png');
        const bsiLogoBlob = await bsiLogoResponse.blob();
        const bsiLogoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(bsiLogoBlob);
        });

        const bsiLogoId = workbook.addImage({
          base64: bsiLogoBase64,
          extension: 'png',
        });

        worksheet.addImage(bsiLogoId, {
          tl: { col: 7.5, row: 0 },
          ext: { width: 90, height: 45 }
        });
      } catch (logoError) {
      }

      // Row 1-2: Empty for logo space
      const logoRow = worksheet.addRow([]);
      logoRow.height = 35;
      worksheet.addRow([]);

      // Row 3: Combined title - matching PDF exactly
      const titleRow = worksheet.addRow(['', '', '', 'Bank Syariah Indonesia - Technical Project Admin Daily Activity Reports', '', '', '', '', '']);
      worksheet.mergeCells('D3:G3');
      titleRow.getCell(4).font = { bold: true, size: 12 };
      titleRow.getCell(4).alignment = { horizontal: 'center' };

      // Row 4: Info - Name
      const nameRow = worksheet.addRow(['', '', 'Name :', userInfo?.full_name || 'N/A', '', '', '', '', '']);
      nameRow.getCell(3).font = { bold: true, size: 10 };
      nameRow.getCell(4).font = { size: 10 };

      // Row 5: Info - Role
      const roleRow = worksheet.addRow(['', '', 'Role :', userInfo?.position || 'Technical Project Admin', '', '', '', '', '']);
      roleRow.getCell(3).font = { bold: true, size: 10 };
      roleRow.getCell(4).font = { size: 10 };

      // Row 6: Info - Periode
      const periodeRow = worksheet.addRow(['', '', 'Periode :', getPeriode(), '', '', '', '', '']);
      periodeRow.getCell(3).font = { bold: true, size: 10 };
      periodeRow.getCell(4).font = { size: 10 };

      // Row 7: Info - Departement
      const deptRow = worksheet.addRow(['', '', 'Departement / Group :', 'Project Management | IT Digital & Development Group (IDG)', '', '', '', '', '']);
      deptRow.getCell(3).font = { bold: true, size: 10 };
      deptRow.getCell(4).font = { size: 10 };

      // Row 8: Empty before table
      worksheet.addRow([]);

      // Row 9-10: 2-row table header (matching PDF exactly)
      // Header Row 1
      const headerRow1 = worksheet.addRow(['No', 'Day', 'Date', 'Time', '', 'Kode Project\nJIRA', 'Nama Project Inisiatif', 'DAILY ACTIVITY', '']);
      const headerRowNum1 = worksheet.rowCount;

      // Header Row 2
      const headerRow2 = worksheet.addRow(['', '', '', 'From', 'To', '', '', '', '']);
      const headerRowNum2 = worksheet.rowCount;

      // Merge cells for 2-row header
      worksheet.mergeCells(`A${headerRowNum1}:A${headerRowNum2}`); // No
      worksheet.mergeCells(`B${headerRowNum1}:B${headerRowNum2}`); // Day
      worksheet.mergeCells(`C${headerRowNum1}:C${headerRowNum2}`); // Date
      worksheet.mergeCells(`D${headerRowNum1}:E${headerRowNum1}`); // Time spans From/To
      worksheet.mergeCells(`F${headerRowNum1}:F${headerRowNum2}`); // Kode Project
      worksheet.mergeCells(`G${headerRowNum1}:G${headerRowNum2}`); // Nama Project
      worksheet.mergeCells(`H${headerRowNum1}:H${headerRowNum2}`); // Daily Activity

      // Style header rows
      [headerRow1, headerRow2].forEach(row => {
        row.eachCell((cell, colNumber) => {
          if (colNumber <= 8) {
            cell.font = { bold: true, size: 9 };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6E6E6' } };
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
          }
        });
      });

      // Data rows
      reportData.forEach((e) => {
        const isGray = e.isWeekend || e.isHoliday || e.status === 'libur' || e.status === 'cuti' || e.status === 'sakit' || e.status === 'weekend';
        const dataRow = worksheet.addRow([
          e.no !== '' ? e.no : '',
          e.day || '',
          e.date ? formatDateID(e.date) : '',
          e.timeFrom || '',
          e.timeTo || '',
          e.jiraCode || '-',
          e.initiative || '-',
          e.activity || '-',
          ''
        ]);

        dataRow.eachCell((cell, colNumber) => {
          if (colNumber <= 8) {
            cell.font = { size: 8 };
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
            if (isGray) {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCDCDC' } };
            }
            if (colNumber <= 5) {
              cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else {
              cell.alignment = { vertical: 'middle', wrapText: true };
            }
          }
        });
      });

      // Empty rows before summary
      worksheet.addRow([]);
      worksheet.addRow([]);

      // Statistics section (left) and Signature section (right) - matching PDF
      // Row 1: JUMLAH + Signature header
      const jumlahRow = worksheet.addRow(['JUMLAH', `: ${stats.masuk}`, '', '', '', 'Resource', 'Team Leader', 'Dept Head', '']);
      jumlahRow.getCell(1).font = { size: 9 };
      jumlahRow.getCell(2).font = { size: 9 };
      // Signature header
      jumlahRow.getCell(6).font = { bold: true, size: 9 };
      jumlahRow.getCell(6).alignment = { horizontal: 'center' };
      jumlahRow.getCell(6).border = { top: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' }, bottom: { style: 'thin' } };
      jumlahRow.getCell(7).font = { bold: true, size: 9 };
      jumlahRow.getCell(7).alignment = { horizontal: 'center' };
      jumlahRow.getCell(7).border = { top: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' }, bottom: { style: 'thin' } };
      jumlahRow.getCell(8).font = { bold: true, size: 9 };
      jumlahRow.getCell(8).alignment = { horizontal: 'center' };
      jumlahRow.getCell(8).border = { top: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' }, bottom: { style: 'thin' } };

      // Empty row for signature space
      const emptyRow = worksheet.addRow(['', '', '', '', '', '', '', '', '']);
      emptyRow.height = 25;
      emptyRow.getCell(6).border = { left: { style: 'thin' }, right: { style: 'thin' } };
      emptyRow.getCell(7).border = { left: { style: 'thin' }, right: { style: 'thin' } };
      emptyRow.getCell(8).border = { left: { style: 'thin' }, right: { style: 'thin' } };

      // Row 2: SAKIT + empty signature
      const sakitRow = worksheet.addRow(['SAKIT', `: ${stats.sakit}`, '', '', '', '', '', '', '']);
      sakitRow.getCell(1).font = { size: 9 };
      sakitRow.getCell(2).font = { size: 9 };
      sakitRow.getCell(6).border = { left: { style: 'thin' }, right: { style: 'thin' } };
      sakitRow.getCell(7).border = { left: { style: 'thin' }, right: { style: 'thin' } };
      sakitRow.getCell(8).border = { left: { style: 'thin' }, right: { style: 'thin' } };

      // Row 3: CUTI + signature lines
      const cutiRow = worksheet.addRow(['CUTI', `: ${stats.cuti}`, '', '', '', '____________', '____________', '____________', '']);
      cutiRow.getCell(1).font = { size: 9 };
      cutiRow.getCell(2).font = { size: 9 };
      cutiRow.getCell(6).alignment = { horizontal: 'center' };
      cutiRow.getCell(6).border = { left: { style: 'thin' }, right: { style: 'thin' } };
      cutiRow.getCell(7).alignment = { horizontal: 'center' };
      cutiRow.getCell(7).border = { left: { style: 'thin' }, right: { style: 'thin' } };
      cutiRow.getCell(8).alignment = { horizontal: 'center' };
      cutiRow.getCell(8).border = { left: { style: 'thin' }, right: { style: 'thin' } };

      // Row 4: TOTAL + names
      const totalRow = worksheet.addRow(['TOTAL', `: ${stats.total}`, '', '', '', `(${userInfo?.full_name || 'N/A'})`, '(Ressa Fauzi)', '(Muhammad Akbar)', '']);
      totalRow.getCell(1).font = { bold: true, size: 9 };
      totalRow.getCell(1).border = { top: { style: 'thin' } };
      totalRow.getCell(2).font = { bold: true, size: 9 };
      totalRow.getCell(2).border = { top: { style: 'thin' } };
      totalRow.getCell(6).font = { bold: true, size: 9 };
      totalRow.getCell(6).alignment = { horizontal: 'center' };
      totalRow.getCell(6).border = { left: { style: 'thin' }, right: { style: 'thin' }, bottom: { style: 'thin' } };
      totalRow.getCell(7).font = { bold: true, size: 9 };
      totalRow.getCell(7).alignment = { horizontal: 'center' };
      totalRow.getCell(7).border = { left: { style: 'thin' }, right: { style: 'thin' }, bottom: { style: 'thin' } };
      totalRow.getCell(8).font = { bold: true, size: 9 };
      totalRow.getCell(8).alignment = { horizontal: 'center' };
      totalRow.getCell(8).border = { left: { style: 'thin' }, right: { style: 'thin' }, bottom: { style: 'thin' } };

      // Generate file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Timesheet_${userInfo?.full_name}_${filterMonth}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);

      showToast("Excel berhasil diexport dengan logo! 📊");
    } catch (error) {
      console.error('Excel export error:', error);
      showToast("Gagal export Excel: " + error.message, "error");
    }
  };

  // Helper function to load image as base64
  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = src;
    });
  };

  // EXPORT PDF - Template ADIDATA BSI Professional with Logos
  const exportPDF = async () => {
    try {
      // Debug: Log reportData before PDF generation

      // Log grouped data by date to see if multiple entries exist
      const groupedByDate = {};
      reportData.forEach(r => {
        const date = r.displayDate || r.date;
        if (!groupedByDate[date]) groupedByDate[date] = [];
        groupedByDate[date].push(r);
      });

      const doc = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      // eslint-disable-next-line no-unused-vars
      const pageHeight = doc.internal.pageSize.getHeight();

      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, pageWidth, 35, 'F');

      // Load logos
      let adidataLogo = null;
      let bsiLogo = null;

      try {
        adidataLogo = await loadImage('/assets/logos/adidata.png');
      } catch (e) {
      }

      try {
        bsiLogo = await loadImage('/assets/logos/logobsi.png');
      } catch (e) {
      }

      // Add ADIDATA Logo (left) - smaller for portrait
      if (adidataLogo) {
        doc.addImage(adidataLogo, 'PNG', 14, 8, 25, 12);
      } else {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(220, 53, 69);
        doc.text('ADIDATA', 14, 14);
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text('DELIVERS IT SOLUTION', 14, 18);
      }

      // Add BSI Logo (right) - smaller for portrait
      if (bsiLogo) {
        doc.addImage(bsiLogo, 'PNG', pageWidth - 45, 8, 35, 10);
      } else {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 128, 0);
        doc.text('BSI', pageWidth - 25, 14);
        doc.setFontSize(5);
        doc.setFont('helvetica', 'normal');
        doc.text('BANK SYARIAH', pageWidth - 30, 17);
        doc.text('INDONESIA', pageWidth - 28, 20);
      }

      // Title - positioned BELOW logos
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Bank Syariah Indonesia', pageWidth / 2, 26, { align: 'center' });
      doc.setFontSize(9);
      doc.text('Technical Project Admin Daily Activity Reports', pageWidth / 2, 31, { align: 'center' });

      // Orange line separator - below title
      doc.setDrawColor(248, 156, 56);
      doc.setLineWidth(1);
      doc.line(14, 35, pageWidth - 14, 35);

      // Employee Info - matching reference style with right-aligned labels
      doc.setFontSize(9);
      let yPos = 40;
      const labelX = 55; // Right edge of labels
      const valueX = 58; // Left edge of values

      // Name:
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.text('Name :', labelX, yPos, { align: 'right' });
      doc.setFont('helvetica', 'bold');
      doc.text(`${userInfo?.full_name || 'N/A'}`, valueX, yPos);

      yPos += 5;
      doc.setFont('helvetica', 'normal');
      doc.text('Role :', labelX, yPos, { align: 'right' });
      doc.text(`${userInfo?.position || 'Technical Project Admin'}`, valueX, yPos);

      yPos += 5;
      doc.text('Periode :', labelX, yPos, { align: 'right' });
      doc.text(`${getPeriode()}`, valueX, yPos);

      yPos += 5;
      doc.setFont('helvetica', 'bold');
      doc.text('Departement / Group :', labelX, yPos, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.text('Project Management | IT Digital & Development Group (IDG)', valueX, yPos);

      // Table with soft grey header
      autoTable(doc, {
        startY: yPos + 8,
        head: [
          [
            { content: 'No', rowSpan: 2 },
            { content: 'Day', rowSpan: 2 },
            { content: 'Date', rowSpan: 2 },
            { content: 'Time', colSpan: 2 },
            { content: 'Kode Project\nJIRA', rowSpan: 2 },
            { content: 'Nama Project Inisiatif', rowSpan: 2 },
            { content: 'DAILY ACTIVITY', rowSpan: 2 }
          ],
          ['From', 'To']
        ],
        body: reportData.map((e) => {
          // For first row or single entry day - show all info
          if (e.isFirstRow) {
            return [
              String(e.no),
              e.day || '',
              e.date ? formatDateID(e.date) : '',
              e.timeFrom || '-',
              e.timeTo || '-',
              e.jiraCode || '-',
              e.initiative || '-',
              e.activity || '-',
            ];
          }
          // For subsequent rows of same day - show empty for merged columns
          else {
            return [
              '',
              '',
              '',
              '',
              '',
              e.jiraCode || '-',
              e.initiative || '-',
              e.activity || '-',
            ];
          }
        }),
        theme: 'grid',
        headStyles: {
          fillColor: [230, 230, 230], // Soft grey color
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'center',
          valign: 'middle',
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
        },
        bodyStyles: {
          fontSize: 6,
          cellPadding: 1.5,
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
        },
        alternateRowStyles: {
          // No fillColor - let willDrawCell handle row coloring for sick/leave/weekend
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 7 },   // No
          1: { halign: 'center', cellWidth: 11 },  // Day
          2: { halign: 'center', cellWidth: 21 },    // Date - wider for full date
          3: { halign: 'center', cellWidth: 11 },  // Time From
          4: { halign: 'center', cellWidth: 11 },  // Time To
          5: { halign: 'center', cellWidth: 14 },  // Kode Project
          6: { halign: 'center', cellWidth: 20 },  // Nama Project
          7: { halign: 'left', valign: 'top', cellWidth: 'auto' }, // Daily Activity - supports multi-line
        },
        margin: { left: 14, right: 14 },
        tableLineWidth: 0.1,
        tableLineColor: [0, 0, 0],
        didParseCell: function (data) {
          if (data.section === 'body') {
            const rowData = reportData[data.row.index];

            // Apply soft gray background for weekend, cuti, sakit, or libur
            const isSickOrLeave = rowData && (
              rowData.isWeekend ||
              rowData.isHoliday ||
              rowData.status === 'weekend' ||
              rowData.status === 'libur' ||
              rowData.status === 'cuti' ||
              rowData.status === 'sakit' ||
              rowData.activity?.toLowerCase().includes('sakit') ||
              rowData.activity?.toLowerCase().includes('izin sakit') ||
              rowData.initiative === 'off day' ||
              rowData.initiative === 'Libur'
            );
            if (isSickOrLeave) {
              data.cell.styles.fillColor = [220, 220, 220]; // Gray background
            }
          }
        },
        willDrawCell: function (data) {
          if (data.section === 'body') {
            const rowData = reportData[data.row.index];
            const colIndex = data.column.index;

            // For merged columns (0-4) on non-first rows, hide top border
            if (colIndex <= 4 && rowData && !rowData.isFirstRow) {
              const nextRowData = reportData[data.row.index + 1];
              const isLastInGroup = !nextRowData || nextRowData.isFirstRow;

              data.cell.styles.lineWidth = {
                top: 0,
                right: 0.1,
                bottom: isLastInGroup ? 0.1 : 0,
                left: 0.1
              };
            }

            // For first row with multiple entries, remove bottom border
            if (colIndex <= 4 && rowData && rowData.isFirstRow && rowData.rowCount > 1) {
              data.cell.styles.lineWidth = {
                top: 0.1,
                right: 0.1,
                bottom: 0,
                left: 0.1
              };
            }
          }
        },
      });

      const finalY = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || 100) + 8;

      // Statistics section on LEFT side - compact for portrait
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('JUMLAH HARI MASUK', 14, finalY);
      doc.text(`: ${stats.masuk}`, 48, finalY);
      doc.text('SAKIT', 14, finalY + 4);
      doc.text(`: ${stats.sakit}`, 48, finalY + 4);
      doc.text('CUTI', 14, finalY + 8);
      doc.text(`: ${stats.cuti}`, 48, finalY + 8);

      // Draw line above TOTAL
      doc.line(14, finalY + 11, 55, finalY + 11);

      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL', 14, finalY + 15);
      doc.text(`: ${stats.total}`, 48, finalY + 15);

      // Signature section on RIGHT side - adjusted for portrait (narrower)
      const signatureTableWidth = pageWidth - 80; // Dynamic width based on page
      const tableStartX = 65; // Start after stats section
      const colWidth = signatureTableWidth / 3;
      const signatureY = finalY - 2;
      const headerHeight = 6;
      const bodyHeight = 20;

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.1);

      // Draw header row with border
      doc.rect(tableStartX, signatureY, signatureTableWidth, headerHeight);
      doc.line(tableStartX + colWidth, signatureY, tableStartX + colWidth, signatureY + headerHeight);
      doc.line(tableStartX + colWidth * 2, signatureY, tableStartX + colWidth * 2, signatureY + headerHeight);

      // Header text - BOLD
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text('Resource', tableStartX + colWidth / 2, signatureY + 4, { align: 'center' });
      doc.text('Team Leader', tableStartX + colWidth * 1.5, signatureY + 4, { align: 'center' });
      doc.text('Dept Head', tableStartX + colWidth * 2.5, signatureY + 4, { align: 'center' });

      // Draw body row with border
      const bodyY = signatureY + headerHeight;
      doc.rect(tableStartX, bodyY, signatureTableWidth, bodyHeight);
      doc.line(tableStartX + colWidth, bodyY, tableStartX + colWidth, bodyY + bodyHeight);
      doc.line(tableStartX + colWidth * 2, bodyY, tableStartX + colWidth * 2, bodyY + bodyHeight);

      // Draw lines above names (signature lines)
      const lineAboveNameY = bodyY + bodyHeight - 6;
      const lineMargin = 3;
      doc.line(tableStartX + lineMargin, lineAboveNameY, tableStartX + colWidth - lineMargin, lineAboveNameY);
      doc.line(tableStartX + colWidth + lineMargin, lineAboveNameY, tableStartX + colWidth * 2 - lineMargin, lineAboveNameY);
      doc.line(tableStartX + colWidth * 2 + lineMargin, lineAboveNameY, tableStartX + colWidth * 3 - lineMargin, lineAboveNameY);

      // Signature names below the lines
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text(`(${userInfo?.full_name || 'N/A'})`, tableStartX + colWidth / 2, bodyY + bodyHeight - 2, { align: 'center' });
      doc.text('(Ressa Fauzi)', tableStartX + colWidth * 1.5, bodyY + bodyHeight - 2, { align: 'center' });
      doc.text('(Muhammad Akbar)', tableStartX + colWidth * 2.5, bodyY + bodyHeight - 2, { align: 'center' });

      doc.save(`Timesheet_${userInfo?.full_name}_${filterMonth}.pdf`);
      showToast("PDF berhasil diexport! 📑");
    } catch (error) {
      console.error('Error generating PDF:', error);
      showToast("Gagal membuat PDF: " + error.message, "error");
    }
  };

  // PRINT
  const handlePrint = () => {
    if (isMobileDevice()) {
      showToast('💡 Tip: Untuk hasil terbaik di mobile, gunakan Export Excel atau PDF', 'info');
      setTimeout(() => {
        handleMobileFriendlyPrint({
          documentTitle: `Timesheet_${userInfo?.full_name}`,
          onAfterPrint: () => showToast('Dokumen berhasil dicetak')
        });
      }, 1000);
    } else {
      handleDesktopPrint();
    }
  };

  const handleDesktopPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Timesheet_${userInfo?.full_name}`,
    onAfterPrint: () => {
      showToast('Dokumen berhasil dicetak');
    }
  });

  return (
    <div className="min-h-screen bg-white relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100/30 via-blue-100/30 to-blue-100/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-blue-100/30 via-pink-100/30 to-blue-100/30 rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      <main className="relative z-10 p-4 sm:p-6 lg:p-8 flex-1">
        <div className="max-w-7xl mx-auto" ref={printRef}>

          {/* TOAST */}
          {toast.message && (
            <div
              className={`mb-4 px-4 py-3 rounded-lg shadow-md text-sm font-medium ${toast.type === "error"
                ? "bg-red-100 text-red-700 border border-red-300"
                : "bg-green-100 text-green-700 border border-green-300"
                }`}
            >
              {toast.message}
            </div>
          )}

          {/* Header Banner */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-500 rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 mb-6 text-white">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => navigate(-1)}
                  className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-1.5 sm:p-2 rounded-lg shadow-md transition-all"
                >
                  <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <div>
                  <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold">Timesheet Report</h1>
                  <p className="text-blue-100 text-xs sm:text-sm mt-0.5 sm:mt-1">Laporan aktivitas kerja bulanan - Data dari Absensi</p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <input
                    type="month"
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    className="w-full pl-3 sm:pl-10 pr-3 py-1.5 sm:py-2 text-sm sm:text-base border-0 rounded-lg shadow-md text-slate-700 font-medium focus:ring-2 focus:ring-blue-300 min-h-[42px] bg-white"
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* STATISTIK CARDS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white shadow-md hover:shadow-sm rounded-xl p-4 sm:p-6 border-l-4 border-blue-600 transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-xs sm:text-sm font-medium">Hari Masuk</p>
                  <p className="text-2xl sm:text-3xl font-semibold text-blue-600 mt-1">{stats.masuk}</p>
                </div>
                <div className="bg-blue-100 p-2 sm:p-3 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white shadow-md hover:shadow-sm rounded-xl p-4 sm:p-6 border-l-4 border-red-600 transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-xs sm:text-sm font-medium">Sakit</p>
                  <p className="text-2xl sm:text-3xl font-semibold text-red-600 mt-1">{stats.sakit}</p>
                </div>
                <div className="bg-red-100 p-2 sm:p-3 rounded-lg text-2xl sm:text-4xl">🏥</div>
              </div>
            </div>

            <div className="bg-white shadow-md hover:shadow-sm rounded-xl p-4 sm:p-6 border-l-4 border-yellow-600 transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-xs sm:text-sm font-medium">Cuti</p>
                  <p className="text-2xl sm:text-3xl font-semibold text-yellow-600 mt-1">{stats.cuti}</p>
                </div>
                <div className="bg-yellow-100 p-2 sm:p-3 rounded-lg text-2xl sm:text-4xl">🏖️</div>
              </div>
            </div>

            <div className="bg-white shadow-md hover:shadow-sm rounded-xl p-4 sm:p-6 border-l-4 border-green-600 transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-xs sm:text-sm font-medium">Total</p>
                  <p className="text-2xl sm:text-3xl font-semibold text-green-600 mt-1">{stats.total}</p>
                </div>
                <div className="bg-green-100 p-2 sm:p-3 rounded-lg">
                  <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                </div>
              </div>
            </div>
          </div>

          {/* UPCOMING HOLIDAYS BANNER */}
          {upcomingHolidays.length > 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="bg-amber-100 p-2 rounded-lg">
                  <span className="text-2xl">📅</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800 mb-2">
                    Libur & Cuti Bersama Bulan Ini ({upcomingHolidays.length} hari)
                  </h3>
                  <div className="space-y-1.5">
                    {upcomingHolidays.map((holiday, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <span className="bg-amber-200 text-amber-800 px-2 py-0.5 rounded text-xs font-medium">
                          {holiday.dayName}
                        </span>
                        <span className="text-amber-700 font-medium">{holiday.formattedDate}</span>
                        <span className="text-amber-600">—</span>
                        <span className="text-amber-800">{holiday.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TABLE WRAPPER */}
          <div className="bg-white shadow-sm rounded-2xl border border-gray-100">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-800">Data Timesheet - {getMonthName(filterMonth)}</h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-1">Data diambil dari absensi dan timesheet harian</p>
              </div>

              {reportData.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={exportCSV}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-green-700 hover:to-green-800 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm shadow-md font-medium transition-all"
                  >
                    📄 CSV
                  </button>

                  <button
                    onClick={exportExcel}
                    className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm shadow-md font-medium transition-all"
                  >
                    📊 Excel
                  </button>

                  <button
                    onClick={exportPDF}
                    className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-red-700 hover:to-red-800 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm shadow-md font-medium transition-all"
                  >
                    📑 PDF
                  </button>

                  <button
                    onClick={handlePrint}
                    className="bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-700 hover:to-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm shadow-md font-medium transition-all"
                  >
                    🖨️ Print
                  </button>
                </div>
              )}
            </div>

            {/* TABLE */}
            {loadingData ? (
              <TableSkeleton rows={10} />
            ) : reportData.length === 0 ? (
              <div className="p-6 text-center">
                <div className="text-6xl mb-4">📋</div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Belum ada data</h3>
                <p className="text-slate-500 text-sm">Data absensi dan timesheet untuk bulan {getMonthName(filterMonth)} belum tersedia.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm divide-y divide-gray-200">
                  <thead className="bg-gray-50 text-slate-600">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold">No</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold">Day</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold">Date</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold">Time From</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold">Time To</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold">Kode Project JIRA</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold">Nama Project Inisiatif</th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold">Daily Activity</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 bg-white">
                    {reportData.map((row, index) => (
                      <tr
                        key={index}
                        className={`hover:bg-gray-50 ${row.status === 'cuti' || row.status === 'sakit' ? 'bg-yellow-50' :
                          row.isWeekend ? 'bg-gray-50' : ''
                          } ${!row.isFirstRow ? 'border-t-0' : ''}`}
                      >
                        <td className={`px-3 sm:px-6 py-3 text-xs sm:text-sm ${!row.isFirstRow ? 'border-t border-dashed border-gray-200' : ''}`}>
                          {row.no !== '' ? row.no : ''}
                        </td>
                        <td className={`px-3 sm:px-6 py-3 text-xs sm:text-sm font-medium ${!row.isFirstRow ? 'border-t border-dashed border-gray-200' : ''}`}>
                          {row.day || ''}
                        </td>
                        <td className={`px-3 sm:px-6 py-3 text-xs sm:text-sm ${!row.isFirstRow ? 'border-t border-dashed border-gray-200' : ''}`}>
                          {row.date ? formatDateID(row.date) : ''}
                        </td>
                        <td className={`px-3 sm:px-6 py-3 text-xs sm:text-sm ${!row.isFirstRow ? 'border-t border-dashed border-gray-200' : ''}`}>
                          {row.timeFrom || (row.isFirstRow && !row.isWeekend && !row.isHoliday && row.status !== 'cuti' && row.status !== 'sakit' ? '-' : '')}
                        </td>
                        <td className={`px-3 sm:px-6 py-3 text-xs sm:text-sm ${!row.isFirstRow ? 'border-t border-dashed border-gray-200' : ''}`}>
                          {row.timeTo || (row.isFirstRow && !row.isWeekend && !row.isHoliday && row.status !== 'cuti' && row.status !== 'sakit' ? '-' : '')}
                        </td>
                        <td className={`px-3 sm:px-6 py-3 text-xs sm:text-sm font-mono font-semibold text-blue-600 ${!row.isFirstRow ? 'border-t border-dashed border-gray-200' : ''}`}>
                          {row.jiraCode || (row.isWeekend || row.isHoliday || row.status === 'cuti' || row.status === 'sakit' ? '' : '-')}
                        </td>
                        <td className={`px-3 sm:px-6 py-3 text-xs sm:text-sm ${!row.isFirstRow ? 'border-t border-dashed border-gray-200' : ''}`}>
                          {row.initiative === 'off day' ? (
                            <span className="text-gray-500 italic">{row.initiative}</span>
                          ) : row.initiative === 'Libur' ? (
                            <span className="text-orange-600 font-medium">{row.initiative}</span>
                          ) : (
                            row.initiative || (row.isWeekend || row.isHoliday || row.status === 'cuti' || row.status === 'sakit' ? '' : '-')
                          )}
                        </td>
                        <td className={`px-3 sm:px-6 py-3 text-xs sm:text-sm ${!row.isFirstRow ? 'border-t border-dashed border-gray-200' : ''}`}>
                          {row.statusText ? (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.status === 'cuti' ? 'bg-yellow-100 text-yellow-800' :
                              row.status === 'sakit' ? 'bg-red-100 text-red-800' :
                              row.status === 'libur' ? 'bg-orange-100 text-orange-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                              {row.statusText}
                            </span>
                          ) : (
                            <span className="whitespace-pre-line">{row.activity || (row.isWeekend || row.isHoliday ? '' : '-')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summary Footer */}
            {reportData.length > 0 && (
              <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">JUMLAH HARI MASUK:</span>
                    <span className="ml-2 font-semibold text-blue-600">{stats.masuk}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">SAKIT:</span>
                    <span className="ml-2 font-semibold text-red-600">{stats.sakit}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">CUTI:</span>
                    <span className="ml-2 font-semibold text-yellow-600">{stats.cuti}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">TOTAL:</span>
                    <span className="ml-2 font-semibold text-green-600">{stats.total}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
